import { createTheme, alpha } from "@mui/material/styles";

// ============================================================================
// DESIGN TOKENS - Sistema di Design Centralizzato
// ============================================================================

const designTokens = {
  colors: {
    navy: {
      50: '#E8EDF3',
      100: '#C5D2E1',
      200: '#9EB4CD',
      300: '#7796B9',
      400: '#5A7FAA',
      500: '#3D689B', // Primary Light
      600: '#375F93',
      700: '#2F5389',
      800: '#27477F',
      900: '#1A336D', // Primary Dark Enhanced
    },
    gold: {
      50: '#FDF8ED',
      100: '#FAEED2',
      200: '#F7E3B5',
      300: '#F3D897',
      400: '#F0D080',
      500: '#EDC869', // Secondary base
      600: '#E8BA4D', // Secondary Light (più vivace)
      700: '#C9A24D', // Mantenuto
      800: '#A68840',
      900: '#7D6630',
    },
    semantic: {
      success: { light: '#10B981', main: '#059669', dark: '#047857' },
      warning: { light: '#F59E0B', main: '#D97706', dark: '#B45309' },
      error: { light: '#EF4444', main: '#EF4444', dark: '#B91C1C' },
      info: { light: '#3B82F6', main: '#2563EB', dark: '#1D4ED8' },
    },
    neutral: {
      light: {
        50: '#FAFBFC',
        100: '#F6F7F9',
        200: '#E3E6EA',
        300: '#CDD2D9',
        400: '#A4ABB6',
        500: '#5F6B7A',
        600: '#4A5566',
        700: '#354052',
        800: '#1E2A3B',
        900: '#0E1A2B',
      },
      // DARK MODE - Palette ridefinita con tonalità slate/blue moderne
      dark: {
        50: '#0A0F16',
        100: '#111827',
        150: '#1A202C',
        200: '#1E293B',
        250: '#273548',
        300: '#334155',
        400: '#475569',
        500: '#64748B',
        600: '#94A3B8',
        700: '#CBD5E1',
        800: '#E2E8F0',
        900: '#F1F5F9',
      },
    },
  },
  
  elevation: {
    light: {
      1: '0 1px 3px rgba(30, 55, 92, 0.08), 0 1px 2px rgba(30, 55, 92, 0.06)',
      2: '0 4px 6px rgba(30, 55, 92, 0.07), 0 2px 4px rgba(30, 55, 92, 0.06)',
      3: '0 10px 15px rgba(30, 55, 92, 0.08), 0 4px 6px rgba(30, 55, 92, 0.05)',
      4: '0 20px 25px rgba(30, 55, 92, 0.10), 0 10px 10px rgba(30, 55, 92, 0.04)',
    },

    dark: {
      1: '0 1px 3px rgba(0, 0, 0, 0.4), 0 1px 2px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(71, 85, 105, 0.1)',
      2: '0 4px 6px rgba(0, 0, 0, 0.5), 0 2px 4px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(71, 85, 105, 0.15)',
      3: '0 10px 15px rgba(0, 0, 0, 0.6), 0 4px 6px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(71, 85, 105, 0.2)',
      4: '0 20px 25px rgba(0, 0, 0, 0.7), 0 10px 10px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(71, 85, 105, 0.25)',
      glow: 'rgba(71, 85, 105, 0.3)', 
    }
  },
};

// ============================================================================
// CONFIGURAZIONE TIPOGRAFIA
// ============================================================================

const typography = {
  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
  h1: { fontWeight: 800, fontSize: '2.5rem', letterSpacing: '-0.02em', lineHeight: 1.2 },
  h2: { fontWeight: 700, fontSize: '2rem', letterSpacing: '-0.015em', lineHeight: 1.3 },
  h3: { fontWeight: 700, fontSize: '1.75rem', letterSpacing: '-0.01em', lineHeight: 1.3 },
  h4: { fontWeight: 600, fontSize: '1.5rem', letterSpacing: '-0.008em', lineHeight: 1.4 },
  h5: { fontWeight: 600, fontSize: '1.25rem', letterSpacing: '-0.005em', lineHeight: 1.4 },
  h6: { fontWeight: 600, fontSize: '1.125rem', letterSpacing: '0', lineHeight: 1.5 },
  subtitle1: { fontWeight: 500, fontSize: '1rem', lineHeight: 1.6 },
  subtitle2: { fontWeight: 500, fontSize: '0.875rem', lineHeight: 1.57 },
  body1: { fontWeight: 400, fontSize: '1rem', lineHeight: 1.6 },
  body2: { fontWeight: 400, fontSize: '0.875rem', lineHeight: 1.6 },
  button: { fontWeight: 600, fontSize: '0.875rem', textTransform: 'none', letterSpacing: '0.02em' },
  caption: { fontWeight: 400, fontSize: '0.75rem', lineHeight: 1.66, letterSpacing: '0.03em' },
  overline: { fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em' },
};

// ============================================================================
// GENERATORE TEMA
// ============================================================================

export const getTheme = (mode) => {
  const isLight = mode === 'light';
  const colors = designTokens.colors;
  const neutral = isLight ? colors.neutral.light : colors.neutral.dark;
  const semanticLighter = (semanticColor) => (isLight ? alpha(semanticColor.light, 0.12) : alpha(semanticColor.light, 0.18));
  
  return createTheme({
    palette: {
      mode,
      primary: {
        main: isLight ? colors.navy[500] : colors.navy[400],
        light: isLight ? colors.navy[300] : colors.navy[300],
        dark: isLight ? colors.navy[900] : colors.navy[600],
        lighter: isLight ? alpha(colors.navy[500], 0.1) : alpha(colors.navy[400], 0.18),
        soft: isLight ? alpha(colors.navy[500], 0.08) : alpha(colors.navy[400], 0.14),
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: colors.gold[700],
        light: colors.gold[600],
        dark: colors.gold[800],
        lighter: isLight ? alpha(colors.gold[600], 0.16) : alpha(colors.gold[600], 0.22),
        soft: isLight ? alpha(colors.gold[600], 0.1) : alpha(colors.gold[600], 0.16),
        contrastText: isLight ? colors.navy[900] : '#FFFFFF',
      },
      success: {
        ...colors.semantic.success,
        lighter: semanticLighter(colors.semantic.success),
      },
      warning: {
        ...colors.semantic.warning,
        lighter: semanticLighter(colors.semantic.warning),
      },
      error: {
        ...colors.semantic.error,
        lighter: semanticLighter(colors.semantic.error),
      },
      info: {
        ...colors.semantic.info,
        lighter: semanticLighter(colors.semantic.info),
      },
      background: {
        default: isLight ? neutral[100] : neutral[50],   // #0A0F16
        paper: isLight ? '#FFFFFF' : neutral[100],       // #111827 - Slate-900
        muted: isLight ? neutral[50] : alpha(neutral[200], 0.28),
        panel: isLight ? '#FFFFFF' : alpha(neutral[100], 0.78),
        overlay: isLight ? alpha('#FFFFFF', 0.78) : alpha(neutral[50], 0.72),
      },
      text: {
        primary: isLight ? neutral[900] : neutral[700],     // #CBD5E1 - Slate-300
        secondary: isLight ? neutral[500] : neutral[600],   // #94A3B8 - Slate-400
        disabled: isLight ? neutral[400] : neutral[500],    // #64748B - Slate-500
      },
      divider: isLight ? neutral[200] : alpha(neutral[400], 0.20), // Divisori visibili ma non invadenti
      action: {
        active: isLight ? neutral[600] : neutral[600],
        hover: isLight ? neutral[200] : alpha(neutral[900], 0.08),
        selected: isLight ? alpha(neutral[900], 0.08) : alpha(colors.navy[400], 0.16),
        disabled: isLight ? neutral[300] : neutral[400],
        disabledBackground: isLight ? neutral[200] : alpha(neutral[300], 0.12),
      },
    },
    
    typography,
    
    shape: {
      borderRadius: 12,
    },

    radius: {
      chip: 8,
      control: 10,
      base: 2,
      panel: 2,
      indicator: 3,
      round: 999,
    },
    
    shadows: isLight 
      ? [
          'none',
          designTokens.elevation.light[1],
          designTokens.elevation.light[2],
          designTokens.elevation.light[3],
          designTokens.elevation.light[4],
          designTokens.elevation.light[4],
          designTokens.elevation.light[4],
          designTokens.elevation.light[4],
          designTokens.elevation.light[4],
          designTokens.elevation.light[4],
          designTokens.elevation.light[4],
          designTokens.elevation.light[4],
          designTokens.elevation.light[4],
          designTokens.elevation.light[4],
          designTokens.elevation.light[4],
          designTokens.elevation.light[4],
          designTokens.elevation.light[4],
          designTokens.elevation.light[4],
          designTokens.elevation.light[4],
          designTokens.elevation.light[4],
          designTokens.elevation.light[4],
          designTokens.elevation.light[4],
          designTokens.elevation.light[4],
          designTokens.elevation.light[4],
          designTokens.elevation.light[4],
        ]
      : [
          'none',
          designTokens.elevation.dark[1],
          designTokens.elevation.dark[2],
          designTokens.elevation.dark[3],
          designTokens.elevation.dark[4],
          designTokens.elevation.dark[4],
          designTokens.elevation.dark[4],
          designTokens.elevation.dark[4],
          designTokens.elevation.dark[4],
          designTokens.elevation.dark[4],
          designTokens.elevation.dark[4],
          designTokens.elevation.dark[4],
          designTokens.elevation.dark[4],
          designTokens.elevation.dark[4],
          designTokens.elevation.dark[4],
          designTokens.elevation.dark[4],
          designTokens.elevation.dark[4],
          designTokens.elevation.dark[4],
          designTokens.elevation.dark[4],
          designTokens.elevation.dark[4],
          designTokens.elevation.dark[4],
          designTokens.elevation.dark[4],
          designTokens.elevation.dark[4],
          designTokens.elevation.dark[4],
          designTokens.elevation.dark[4],
        ],
    
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarColor: isLight ? `${neutral[300]} ${neutral[100]}` : `${neutral[500]} ${neutral[100]}`,
            '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
              width: 10,
              height: 10,
            },
            '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
              borderRadius: 10,
              backgroundColor: isLight ? neutral[300] : neutral[500],
              minHeight: 24,
              border: `2px solid ${isLight ? neutral[100] : neutral[100]}`,
            },
            '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
              backgroundColor: isLight ? neutral[400] : neutral[600],
            },
            '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
              borderRadius: 10,
              backgroundColor: isLight ? neutral[100] : neutral[100],
            },
          },
        },
      },
      
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: 10,
            fontWeight: 600,
            padding: '10px 20px',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-1px)',
            },
          },
          contained: {
            boxShadow: isLight ? designTokens.elevation.light[1] : designTokens.elevation.dark[1],
            '&:hover': {
              boxShadow: isLight ? designTokens.elevation.light[2] : designTokens.elevation.dark[2],
            },
            // Dark mode: effetto glow sui pulsanti primari
            ...(!isLight && {
              '&.MuiButton-containedPrimary:hover': {
                boxShadow: `${designTokens.elevation.dark[2]}, ${designTokens.elevation.dark.glow}`,
              },
            }),
          },
          outlined: {
            borderWidth: 1.5,
            '&:hover': {
              borderWidth: 1.5,
              backgroundColor: isLight ? alpha(colors.navy[500], 0.04) : alpha(colors.navy[400], 0.12),
            },
          },
        },
      },
      
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            backgroundImage: 'none',
            ...(mode === 'dark' && {
              border: `1px solid ${alpha(neutral[400], 0.12)}`,
              boxShadow: `inset 0 1px 0 ${alpha(neutral[900], 0.05)}`,
            }),
          },
          elevation1: {
            boxShadow: isLight ? designTokens.elevation.light[1] : designTokens.elevation.dark[1],
          },
          elevation2: {
            boxShadow: isLight ? designTokens.elevation.light[2] : designTokens.elevation.dark[2],
          },
          elevation3: {
            boxShadow: isLight ? designTokens.elevation.light[3] : designTokens.elevation.dark[3],
          },
        },
      },
      
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: isLight ? designTokens.elevation.light[1] : designTokens.elevation.dark[1],
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

            ...(mode === 'dark' && {
              backgroundColor: alpha(neutral[100], 0.6),
              backdropFilter: 'blur(24px) saturate(180%)',
              border: `1px solid ${alpha(neutral[400], 0.15)}`,
              boxShadow: `${designTokens.elevation.dark[1]}, inset 0 1px 0 ${alpha(neutral[900], 0.1)}`,
            }),
            '&:hover': {
              boxShadow: isLight ? designTokens.elevation.light[3] : designTokens.elevation.dark[3],
              transform: 'translateY(-2px)',
              ...(mode === 'dark' && {
                backgroundColor: alpha(neutral[150], 0.8),
                borderColor: alpha(neutral[500], 0.25),
                boxShadow: `${designTokens.elevation.dark[3]}, ${alpha(designTokens.elevation.dark.glow, 0.5)}`,
              }),
            },
          },
        },
      },
      
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: 700,
            fontSize: '0.875rem',
            letterSpacing: '0.02em',
            textTransform: 'uppercase',

            backgroundColor: isLight ? neutral[50] : alpha(neutral[200], 0.05),
            color: isLight ? neutral[800] : neutral[700],
            borderBottom: `2px solid ${isLight ? neutral[200] : alpha(neutral[400], 0.2)}`,
          },
          body: {
            fontSize: '0.9375rem',
            color: isLight ? neutral[800] : neutral[700],
            borderBottom: `1px solid ${isLight ? neutral[200] : alpha(neutral[400], 0.12)}`,
          },
        },
      },
      
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 500,
            borderRadius: 8,

            ...(mode === 'dark' && {
              border: `1px solid ${alpha(neutral[400], 0.2)}`,
            }),
          },
          filled: {
            backgroundColor: isLight ? neutral[100] : alpha(neutral[300], 0.12),
            color: isLight ? neutral[800] : neutral[700],
            '&:hover': {
              backgroundColor: isLight ? neutral[200] : alpha(neutral[300], 0.20),
            },
          },
          outlined: {
            borderColor: isLight ? neutral[300] : alpha(neutral[400], 0.25),
            color: isLight ? neutral[700] : neutral[700],
            '&:hover': {
              backgroundColor: isLight ? alpha(neutral[100], 0.5) : alpha(neutral[300], 0.08),
            },
          },
        },
      },
      
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 10,

              ...(mode === 'dark' && {
                backgroundColor: alpha(neutral[200], 0.04),
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(neutral[400], 0.20),
                },
              }),
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: isLight ? colors.navy[300] : alpha(colors.navy[400], 0.5),
              },
              '&.Mui-focused': {
                ...(mode === 'dark' && {
                  backgroundColor: alpha(neutral[200], 0.08),
                }),
                '& .MuiOutlinedInput-notchedOutline': {
                  borderWidth: 2,
                  borderColor: isLight ? colors.navy[500] : colors.gold[600],
                  ...(mode === 'dark' && {
                    boxShadow: `0 0 0 3px ${alpha(colors.gold[600], 0.12)}`,
                  }),
                },
              },
            },
            '& .MuiInputLabel-root': {
              ...(mode === 'dark' && {
                color: neutral[600],
              }),
              '&.Mui-focused': {
                color: isLight ? colors.navy[500] : colors.gold[600],
              },
            },
          },
        },
      },
      
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            fontWeight: 500,

            ...(mode === 'dark' && {
              border: '1px solid',
            }),
          },
          standardSuccess: {
            backgroundColor: isLight ? alpha(colors.semantic.success.light, 0.1) : alpha(colors.semantic.success.light, 0.12),
            color: isLight ? colors.semantic.success.dark : colors.semantic.success.light,
            ...(mode === 'dark' && {
              borderColor: alpha(colors.semantic.success.light, 0.3),
            }),
          },
          standardError: {
            backgroundColor: isLight ? alpha(colors.semantic.error.light, 0.1) : alpha(colors.semantic.error.light, 0.12),
            color: isLight ? colors.semantic.error.dark : colors.semantic.error.light,
            ...(mode === 'dark' && {
              borderColor: alpha(colors.semantic.error.light, 0.3),
            }),
          },
          standardWarning: {
            backgroundColor: isLight ? alpha(colors.semantic.warning.light, 0.1) : alpha(colors.semantic.warning.light, 0.12),
            color: isLight ? colors.semantic.warning.dark : colors.semantic.warning.light,
            ...(mode === 'dark' && {
              borderColor: alpha(colors.semantic.warning.light, 0.3),
            }),
          },
          standardInfo: {
            backgroundColor: isLight ? alpha(colors.semantic.info.light, 0.1) : alpha(colors.semantic.info.light, 0.12),
            color: isLight ? colors.semantic.info.dark : colors.semantic.info.light,
            ...(mode === 'dark' && {
              borderColor: alpha(colors.semantic.info.light, 0.3),
            }),
          },
        },
      },
      
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isLight ? neutral[800] : neutral[200],
            color: isLight ? '#FFFFFF' : neutral[900],
            fontSize: '0.75rem',
            fontWeight: 500,
            borderRadius: 8,
            padding: '8px 12px',
            boxShadow: isLight ? designTokens.elevation.light[2] : designTokens.elevation.dark[3],
            ...(mode === 'dark' && {
              border: `1px solid ${alpha(neutral[400], 0.2)}`,
              backdropFilter: 'blur(12px)',
            }),
          },
        },
      },
      
      MuiSwitch: {
        styleOverrides: {
          root: {
            ...(mode === 'dark' && {
              '& .MuiSwitch-track': {
                backgroundColor: alpha(neutral[500], 0.3),
                opacity: 1,
              },
              '& .Mui-checked + .MuiSwitch-track': {
                backgroundColor: alpha(colors.navy[400], 0.5),
                opacity: 1,
              },
            }),
          },
        },
      },
      
      MuiList: {
        styleOverrides: {
          root: {
            ...(mode === 'dark' && {
              backgroundColor: 'transparent',
            }),
          },
        },
      },
      
      MuiListItem: {
        styleOverrides: {
          root: {
            ...(mode === 'dark' && {
              '&:hover': {
                backgroundColor: alpha(neutral[300], 0.08),
              },
              '&.Mui-selected': {
                backgroundColor: alpha(colors.navy[400], 0.12),
                '&:hover': {
                  backgroundColor: alpha(colors.navy[400], 0.16),
                },
              },
            }),
          },
        },
      },
      
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: isLight ? neutral[200] : alpha(neutral[400], 0.15),
          },
        },
      },
      
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: isLight ? designTokens.elevation.light[1] : designTokens.elevation.dark[2],
            ...(mode === 'dark' && {
              backgroundColor: alpha(neutral[100], 0.8),
              backdropFilter: 'blur(20px)',
              borderBottom: `1px solid ${alpha(neutral[400], 0.12)}`,
            }),
          },
        },
      },
      
      MuiDrawer: {
        styleOverrides: {
          paper: {
            ...(mode === 'dark' && {
              backgroundColor: alpha(neutral[100], 0.95),
              backdropFilter: 'blur(20px)',
              borderRight: `1px solid ${alpha(neutral[400], 0.12)}`,
            }),
          },
        },
      },
      
      MuiDialog: {
        styleOverrides: {
          paper: {
            ...(mode === 'dark' && {
              backgroundColor: alpha(neutral[100], 0.95),
              backdropFilter: 'blur(24px)',
              border: `1px solid ${alpha(neutral[400], 0.15)}`,
            }),
          },
        },
      },
      
      MuiMenu: {
        styleOverrides: {
          paper: {
            ...(mode === 'dark' && {
              backgroundColor: alpha(neutral[150], 0.95),
              backdropFilter: 'blur(20px)',
              border: `1px solid ${alpha(neutral[400], 0.15)}`,
            }),
          },
        },
      },
    },
  });
};

const theme = getTheme('light');
export default theme;
